/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../chunks/tslib.es6.js";
import "../geometry.js";
import t from "../Graphic.js";
import r from "../request.js";
import o from "../core/Error.js";
import s from "../core/Loadable.js";
import i from "../core/Logger.js";
import { MultiOriginJSONMixin as a } from "../core/MultiOriginJSONSupport.js";
import { isAbortError as n } from "../core/promiseUtils.js";
import { urlToObject as l } from "../core/urlUtils.js";
import { property as d } from "../core/accessorSupport/decorators/property.js";
import "../core/accessorSupport/ensureType.js";
import "../core/arrayUtils.js";
import "../core/has.js";
import { subclass as u } from "../core/accessorSupport/decorators/subclass.js";
import p from "../geometry/Extent.js";
import { fromJSON as c } from "../geometry/support/jsonUtils.js";
import { versionMatches as m, onApplyEditsEvent as y, onUpdateMomentEvent as h, emitApplyEditsEvent as f } from "../layers/mixins/EditBusLayer.js";
import { parse as g } from "../layers/support/arcgisLayerUrl.js";
import v from "./support/TopologyValidationJobInfo.js";
import { networkFromPortalItem as w } from "./support/utils.js";
import { isVersionInEditSession as j, currentSessionId as S } from "../versionManagement/support/versionManagementUtils.js";
import b from "../geometry/SpatialReference.js";
let U = class extends a(s) {
  static fromPortalItem(e) {
    return w(e);
  }
  constructor(e) {
    super(e), this.id = null, this.title = null, this.layerUrl = null, this.dataElement = null, this.fullExtent = null, this.spatialReference = null, this.type = null, this.sourceJSON = null, this.gdbVersion = null, this.historicMoment = null, this._applyEditsHandler = e => {
      const {
          serviceUrl: t,
          gdbVersion: r,
          result: o
        } = e,
        s = t === this.featureServiceUrl,
        i = m(t, r, this.gdbVersion);
      s && i && o.then(e => {
        j(t, r) && (this.historicMoment = e.historicMoment);
      });
    }, this._updateMomentHandler = e => {
      const {
          serviceUrl: t,
          gdbVersion: r,
          moment: o
        } = e,
        s = t === this.featureServiceUrl,
        i = m(t, r, this.gdbVersion);
      s && i && (this.historicMoment = o);
    }, this.when().then(() => {
      this.addHandles([y(this._applyEditsHandler), h(this._updateMomentHandler)]);
    }, () => {});
  }
  initialize() {
    this.when().catch(e => {
      n(e) || i.getLogger(this).error("#load()", `Failed to load layer (title: '${this.title ?? "no title"}', id: '${this.id ?? "no id"}')`, {
        error: e
      });
    });
  }
  get datasetName() {
    return this.dataElement?.name ?? null;
  }
  get owner() {
    return this.dataElement?.userIdentity ?? null;
  }
  get schemaGeneration() {
    return this.dataElement?.schemaGeneration ?? null;
  }
  get parsedUrl() {
    return l(this.layerUrl);
  }
  get featureServiceUrl() {
    return g(this.parsedUrl.path).url.path;
  }
  get networkServiceUrl() {
    return this.featureServiceUrl.replace(/\/FeatureServer/i, "/UtilityNetworkServer");
  }
  get layerId() {
    return g(this.parsedUrl.path).sublayer;
  }
  get networkSystemLayers() {
    return null;
  }
  async load(e) {
    return this.addResolvingPromise(this._fetchDataElement(this.featureServiceUrl, this.layerId.toString(), e)), this.addResolvingPromise(this._fetchLayerMetaData(this.layerUrl, e)), this;
  }
  getLayerIdBySourceId(e) {
    if (this.dataElement) {
      const t = this.dataElement.domainNetworks;
      for (const r of t) {
        for (const t of r.edgeSources ?? []) if (t.sourceId === e) return t.layerId;
        for (const t of r.junctionSources ?? []) if (t.sourceId === e) return t.layerId;
      }
      return null;
    }
    return null;
  }
  async queryNamedTraceConfigurations(e, t) {
    const [{
        queryNamedTraceConfigurations: r
      }, {
        default: o
      }] = await Promise.all([import("../rest/networks/queryNamedTraceConfigurations.js"), import("../rest/networks/support/QueryNamedTraceConfigurationsParameters.js")]),
      s = this.networkServiceUrl,
      i = o.from(e);
    return (await r(s, i, {
      ...t
    }))?.namedTraceConfigurations;
  }
  async validateTopology(e, t) {
    if (!e.validateArea) throw new o("network:undefined-validateArea", "the network must have validateArea defined in the validate network topology parameters.");
    const [{
        validateNetworkTopology: r
      }, {
        default: s
      }] = await Promise.all([import("../rest/networks/validateNetworkTopology.js"), import("../rest/networks/support/ValidateNetworkTopologyParameters.js")]),
      i = s.from(e);
    j(this.featureServiceUrl, this.gdbVersion || null) && (i.sessionID = S), i.gdbVersion = this.gdbVersion;
    const a = this.networkServiceUrl,
      n = this.featureServiceUrl,
      l = f(n, null, this.gdbVersion, !0),
      d = await r(a, i, {
        ...t
      });
    if (d?.serviceEdits) {
      const e = [];
      for (const t of d.serviceEdits) {
        const {
            editedFeatures: r
          } = t,
          o = r?.spatialReference ? new b(r.spatialReference) : null;
        e.push({
          layerId: t.layerId,
          editedFeatures: {
            adds: r?.adds?.map(e => this._createEditedFeature(e, o)) || [],
            updates: r?.updates?.map(e => ({
              original: this._createEditedFeature(e[0], o),
              current: this._createEditedFeature(e[1], o)
            })) || [],
            deletes: r?.deletes?.map(e => this._createEditedFeature(e, o)) || [],
            spatialReference: o
          }
        });
      }
      l.resolve({
        edits: null,
        addedFeatures: [],
        updatedFeatures: [],
        deletedFeatures: [],
        addedAttachments: [],
        updatedAttachments: [],
        deletedAttachments: [],
        editedFeatures: e,
        exceededTransferLimit: !1,
        historicMoment: d.moment
      });
    }
    return d;
  }
  async submitTopologyValidationJob(e, t) {
    if (!e.validateArea) throw new o("network:undefined-validateArea", "the network must have validateArea defined in the validate network topology parameters.");
    if (!this.gdbVersion) throw new o("network:undefined-gdb-version", "version must be defined");
    const [{
        submitValidateNetworkTopologyJob: r
      }, {
        default: s
      }] = await Promise.all([import("../rest/networks/validateNetworkTopology.js"), import("../rest/networks/support/ValidateNetworkTopologyParameters.js")]),
      i = s.from(e);
    j(this.featureServiceUrl, this.gdbVersion || null) && (i.sessionID = S), i.gdbVersion = this.gdbVersion;
    const a = this.networkServiceUrl,
      n = this.featureServiceUrl ? f(this.featureServiceUrl, null, this.gdbVersion, !0) : void 0,
      l = await r(a, i, {
        ...t
      });
    return new v({
      statusUrl: l,
      editsResolver: n
    });
  }
  _createEditedFeature(e, r) {
    return new t({
      attributes: e.attributes,
      geometry: c({
        ...e.geometry,
        spatialReference: r
      })
    });
  }
  async _fetchLayerMetaData(e, t) {
    const o = await r(e, {
      responseType: "json",
      query: {
        f: "json"
      },
      ...t
    });
    this.sourceJSON = o.data, this.read(o.data, {
      origin: "service"
    });
  }
  async _fetchDataElement(e, t, o) {
    if (this.dataElement) return;
    const s = await r(`${e}/queryDataElements`, {
      responseType: "json",
      query: {
        layers: JSON.stringify([t]),
        f: "json"
      },
      ...o
    }).then(e => e.data.layerDataElements?.[0]);
    s && this.read(s, {
      origin: "service"
    });
  }
};
e([d({
  type: String,
  nonNullable: !0,
  json: {
    origins: {
      "web-map": {
        read: !0,
        write: {
          isRequired: !0
        }
      },
      service: {
        read: !0
      }
    },
    read: !1
  }
})], U.prototype, "id", void 0), e([d({
  type: String,
  nonNullable: !0,
  json: {
    origins: {
      "web-map": {
        read: !0,
        write: {
          isRequired: !0
        }
      },
      service: {
        read: {
          source: "name"
        }
      }
    },
    read: !1
  }
})], U.prototype, "title", void 0), e([d({
  type: String,
  nonNullable: !0,
  json: {
    origins: {
      "web-map": {
        read: {
          source: "url"
        },
        write: {
          target: "url",
          isRequired: !0
        }
      }
    },
    read: !1
  }
})], U.prototype, "layerUrl", void 0), e([d({
  type: Object,
  json: {
    origins: {
      service: {
        read: !0
      }
    },
    read: !1
  }
})], U.prototype, "dataElement", void 0), e([d({
  type: p,
  json: {
    origins: {
      service: {
        read: {
          source: "extent"
        }
      }
    },
    read: !1
  }
})], U.prototype, "fullExtent", void 0), e([d({
  type: b,
  json: {
    origins: {
      service: {
        read: {
          source: "extent.spatialReference"
        }
      }
    },
    read: !1
  }
})], U.prototype, "spatialReference", void 0), e([d({
  type: ["utility", "trace"],
  readOnly: !0,
  json: {
    read: !1,
    write: !1
  }
})], U.prototype, "type", void 0), e([d({
  readOnly: !0
})], U.prototype, "datasetName", null), e([d({
  readOnly: !0
})], U.prototype, "owner", null), e([d({
  readOnly: !0
})], U.prototype, "schemaGeneration", null), e([d({
  readOnly: !0
})], U.prototype, "parsedUrl", null), e([d({
  readOnly: !0
})], U.prototype, "featureServiceUrl", null), e([d({
  readOnly: !0
})], U.prototype, "networkServiceUrl", null), e([d({
  readOnly: !0
})], U.prototype, "layerId", null), e([d()], U.prototype, "sourceJSON", void 0), e([d({
  readOnly: !0
})], U.prototype, "networkSystemLayers", null), e([d()], U.prototype, "gdbVersion", void 0), e([d({
  type: Date
})], U.prototype, "historicMoment", void 0), U = e([u("esri.networks.Network")], U);
const k = U;
export { k as default };